import { useState } from 'react';
import { Settings } from 'lucide-react';
import Link from 'next/link';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import type { Me } from '../../../../lib/me';
import { Avatar, Button, ProgressBar } from '../../ui';
import { EarnCreditsOffer } from '../../earn-credits/EarnCreditsOffer';
import { BuyCreditsSheet } from '../../../settings/shared';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';
import { useHideDrawerProgressBarEnabled } from '../../../../lib/hide-drawer-progress-bar-flag';
import styles from './MobileSidebar.module.css';

// The drawer's floor (Figma 8265:84671): the credits card over the account row,
// on a fade of the drawer's darkest step so the list scrolls away underneath.
export function MobileFooter({ me }: { me: Me | null }) {
  return (
    <div className={styles.footer}>
      <GenericEventsRouteProvider value="credits">
        <CreditsCard />
      </GenericEventsRouteProvider>
      <GenericEventsRouteProvider value="account">
        <AccountRow me={me} />
      </GenericEventsRouteProvider>
    </div>
  );
}

// Plan name + its CTA, then the usage line over its bar — or, in the upsell arm, plan name over an
// upsell line with a full-width CTA at the foot (Figma 15375-105152). The CTA is the card's affordance
// (a real DS button), so the card itself isn't a link.
function CreditsCard() {
  const credits = useCreditsSummary();
  const upsell = useHideDrawerProgressBarEnabled();
  const [buyOpen, setBuyOpen] = useState(false);
  if (!credits) return null;

  // The plan name TRUNCATES so the CTA keeps its place. That is the whole mitigation for the fact that
  // "Get more credits" is a long label in a narrow card: measured at this card's content width, the
  // longest translations (tl/ru/hu) leave the plan name very little room, and an ellipsised plan name is
  // a much smaller loss than a button that moves — or one pushed off the card. The CTA sits beside the
  // plan name in EVERY case except the upsell arm, which is the arm's own change.
  // One control, built once: only where it sits and how wide it is differ per arm. `fullWidth` is what
  // the upsell arm's foot needs (Figma 15375-105152: 243 at the card's content width, 30 tall). The
  // control keeps it `tiny` and hugging beside the plan name.
  //
  // Accent, not the black primary — Figma 15375-104637. The drawer is a permanently-dark island
  // (`data-theme="dark"` on MobileSidebar), which is the surface those frames draw.
  //
  // Two arms inside it — the top tier sells a top-up rather than a plan (see `CreditsSummary.upgrade`).
  // The `topup` arm carries no href on purpose: it opens a sheet, and a link would let a long-press
  // offer "open in new tab" onto the picker instead.
  const cta =
    credits.upgrade.kind === 'topup' ? (
      <Button
        color="accent"
        size={upsell ? 'small' : 'tiny'}
        fullWidth={upsell}
        onClick={() => {
          credits.upgrade.onClick();
          setBuyOpen(true);
        }}
      >
        {credits.upgrade.label}
      </Button>
    ) : (
      <Button
        color="accent"
        size={upsell ? 'small' : 'tiny'}
        fullWidth={upsell}
        href={credits.upgrade.route}
        linkAs={credits.upgrade.path}
        onClick={credits.upgrade.onClick}
      >
        {credits.upgrade.label}
      </Button>
    );

  return (
    <div className={styles.creditsCard}>
      {/* Head block. CONTROL: plan name and CTA on one row. UPSELL: the CTA has moved to the foot, so
          the name has the row to itself over the upsell line, 8 apart (Figma 15375-105152).

          The earn-credits offer (red band only, behind its OWN gate) stays here in both arms — it is a
          secondary control beside the primary, and moving it per arm would confound two experiments.

          The CTA is offered on every plan: neither native card gates it on free/paid either, and both
          hide only the whole card (on `creditsCap <= 0`, our "no meter" case — which the upsell arm
          deliberately keeps, so the arm changes the CARD and not which users have one).
          `upgrade.label` is what says Upgrade / Switch / Get more credits. Parity on the PICKER arm is the
          GATE, not the destination — native picks between the Symphony picker and the Wixel page itself
          (behind a confirmation), where web always opens /packages and that page decides. */}
      <div className={upsell ? styles.creditsUpsellHead : undefined}>
        <div className={styles.creditsHead}>
          <span className={styles.planLabel}>{credits.planLabel}</span>
          <span className={styles.creditsActions}>
            <EarnCreditsOffer
              entry={{ kind: 'sidebar_credits', fraction: credits.fraction }}
              pageName="menu"
              variant="mobile"
              size="tiny"
            />
            {!upsell && cta}
          </span>
        </div>
        {/* The upsell line, in the PRIMARY ink — Figma binds both text runs to `Text & Icon/Primary`
            (its variable set carries no secondary). */}
        {upsell && <span className={styles.creditsUpsell}>{credits.upsellSubtitle}</span>}
      </div>
      {/* CONTROL only: the usage line over its meter. The upsell arm drops both — that IS the arm — and
          puts the full-width CTA here instead. */}
      {upsell ? (
        cta
      ) : (
        <ProgressBar
          progress={credits.progress}
          value={credits.fraction * 100}
          label={credits.usageLabel}
          decorative
        />
      )}
      {/* The top tier's top-up opens the same Buy-more-credits SHEET the mobile account card
          uses — a phone gets the sheet, not the desktop modal. */}
      {credits.buyCredits && (
        <BuyCreditsSheet
          open={buyOpen}
          onClose={() => setBuyOpen(false)}
          topup={credits.buyCredits.topup}
          planName={credits.buyCredits.planName}
          checkoutHref={credits.buyCredits.checkoutHref}
        />
      )}
    </div>
  );
}

// The whole row opens Settings (and carries the hidden brand-toggle long press),
// with the design's settings gear as the trailing affordance.
function AccountRow({ me }: { me: Me | null }) {
  const { initials, displayName, photoUrl, onPhotoError, settings } = useAccountSummary(me);
  // Reserve the row's height before `me` resolves so the footer doesn't jump.
  if (!initials) return <div style={{ minHeight: 36 }} />;

  return (
    <Link
      href={settings.route}
      as={settings.path}
      onClick={settings.onClick}
      aria-label={settings.label}
      title={displayName}
      className={styles.accountRow}
    >
      <span className={styles.accountButton}>
        <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />
        <span className={styles.accountName}>{displayName}</span>
      </span>
      <span className={styles.accountGear} aria-hidden>
        <Settings size={16} />
      </span>
    </Link>
  );
}
